import type { SxProps, Theme } from '@mui/material/styles';
import { covConstantsStyles } from '~/modules/covenants/constantsStyles.ts';
import { StatusFilterValue, type TApplicationStatus } from '~/modules/covenants/types.ts';

function resolveSx(style: SxProps<Theme>, theme: Theme): Record<string, unknown> {
  if (!style) {
    return {};
  }

  if (typeof style === 'function') {
    return resolveSx(style(theme), theme);
  }

  if (Array.isArray(style)) {
    return style.reduce<Record<string, unknown>>((accumulator, item) => {
      if (!item) {
        return accumulator;
      }

      return {
        ...accumulator,
        ...resolveSx(item, theme),
      };
    }, {});
  }

  return style as Record<string, unknown>;
}

export function mergeSx(...styles: Array<SxProps<Theme> | null | false | undefined>): SxProps<Theme> {
  return (theme: Theme) =>
    styles.reduce<Record<string, unknown>>((accumulator, style) => {
      if (!style) {
        return accumulator;
      }

      return {
        ...accumulator,
        ...resolveSx(style, theme),
      };
    }, {});
}

export function isNeutralApplicationStatus(status: TApplicationStatus): boolean {
  return status === StatusFilterValue.New || status === StatusFilterValue.Sent;
}

export function getApplicationStatusChipSx(status: TApplicationStatus): SxProps<Theme> {
  return mergeSx(
    covConstantsStyles.statusChip,
    isNeutralApplicationStatus(status) ? covConstantsStyles.processingStatusChip : null
  );
}

export function isPastDeadline(deadline?: string | null, now = new Date()): boolean {
  if (!deadline) {
    return false;
  }

  const deadlineDate = new Date(`${deadline}T00:00:00`);

  if (Number.isNaN(deadlineDate.getTime())) {
    return false;
  }

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  return deadlineDate < today;
}
